import { useCallback, useRef, useState } from "react";
import type { CSSProperties, PointerEvent } from "react";
import { CURSOR_BAR_WIDTH } from "@/components/panels/input-bar-metrics";

const COMMENT_POPOVER_MIN_WIDTH = 240;
const COMMENT_POPOVER_MIN_HEIGHT = 120;
const VIEWPORT_MARGIN = 8;

interface ResizeStart {
  pointerId: number;
  clientX: number;
  clientY: number;
  width: number;
  height: number;
  maxWidth: number;
  maxHeight: number;
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

export function useCommentPopoverResize({
  popover,
  popoverPosition,
}: {
  popover: unknown;
  popoverPosition: { x: number; y: number } | null;
}) {
  const surfaceRef = useRef<HTMLDivElement>(null);
  const resizeStart = useRef<ResizeStart | null>(null);
  const [size, setSize] = useState<{
    key: unknown;
    width: number;
    height: number;
  } | null>(null);
  const activeSize = size?.key === popover ? size : null;

  const handleResizePointerDown = useCallback(
    (e: PointerEvent<HTMLElement>) => {
      const rect = surfaceRef.current?.getBoundingClientRect();
      if (!rect || !popoverPosition) return;
      e.preventDefault();
      e.stopPropagation();
      resizeStart.current = {
        pointerId: e.pointerId,
        clientX: e.clientX,
        clientY: e.clientY,
        width: rect.width,
        height: rect.height,
        maxWidth: Math.max(
          COMMENT_POPOVER_MIN_WIDTH,
          window.innerWidth - popoverPosition.x - VIEWPORT_MARGIN,
        ),
        maxHeight: Math.max(
          COMMENT_POPOVER_MIN_HEIGHT,
          window.innerHeight - popoverPosition.y - VIEWPORT_MARGIN,
        ),
      };
      e.currentTarget.setPointerCapture?.(e.pointerId);
    },
    [popoverPosition],
  );

  const handleResizePointerMove = useCallback(
    (e: PointerEvent<HTMLElement>) => {
      const start = resizeStart.current;
      if (!start || start.pointerId !== e.pointerId) return;
      e.preventDefault();
      setSize({
        key: popover,
        width: clamp(
          start.width + e.clientX - start.clientX,
          COMMENT_POPOVER_MIN_WIDTH,
          start.maxWidth,
        ),
        height: clamp(
          start.height + e.clientY - start.clientY,
          COMMENT_POPOVER_MIN_HEIGHT,
          start.maxHeight,
        ),
      });
    },
    [popover],
  );

  const handleResizePointerUp = useCallback((e: PointerEvent<HTMLElement>) => {
    const start = resizeStart.current;
    if (!start || start.pointerId !== e.pointerId) return;
    resizeStart.current = null;
    if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  }, []);

  const handleResizePointerCancel = useCallback(() => {
    resizeStart.current = null;
  }, []);

  const surfaceStyle: CSSProperties = {
    width: activeSize?.width ?? CURSOR_BAR_WIDTH,
    minWidth: COMMENT_POPOVER_MIN_WIDTH,
    height: activeSize?.height,
    minHeight: COMMENT_POPOVER_MIN_HEIGHT,
    maxWidth: "calc(100vw - 16px)",
    maxHeight: "calc(100vh - 16px)",
  };

  return {
    surfaceRef,
    surfaceStyle,
    resizeHandleProps: {
      onPointerDown: handleResizePointerDown,
      onPointerMove: handleResizePointerMove,
      onPointerUp: handleResizePointerUp,
      onPointerCancel: handleResizePointerCancel,
      onLostPointerCapture: handleResizePointerCancel,
    },
  };
}
